import { useState } from "react";
import { CheckCircle2, Download, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel, Th, Td, btn } from "@/components/ui-kit";
import { SapUpload } from "@/components/sap/SapUpload";

const csv = (r) => {
  const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [["Result", r.result], ["File", r.filename], ["Checked", r.checked_at], ["Rows", r.rows], ["File error", r.file_error],
    ["Missing required columns", (r.columns.missing_required || []).join(" ")], ["Unknown columns (ignored)", (r.columns.unknown || []).join(" ")], [],
    ["SALES_ORDER", "PO_NUMBER", "LINES", "SHIP_TO", "SITE", "RESULT", "ERRORS", "WARNINGS"],
    ...r.orders.map((o) => [o.sales_order, o.po_number, o.lines, o.ship_to, o.site, o.errors.length ? "FAIL" : "PASS", o.errors.join("; "), o.warnings.join("; ")])];
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([lines.map((l) => l.map(q).join(",")).join("\r\n")], { type: "text/csv" }));
  a.download = `SAP_check_${r.filename.replace(/\.[^.]+$/, "")}.csv`;
  a.click();
};

const Cols = ({ c }) => (
  <div className="grid gap-3 p-5 text-xs sm:grid-cols-2">
    <div><span className="font-mono uppercase text-slate-500">Recognised columns</span><p className="mt-1 text-emerald-300" data-testid="check-cols-found">{(c.found || []).join(", ") || "—"}</p></div>
    <div><span className="font-mono uppercase text-slate-500">Missing required</span><p className="mt-1 text-rose-300" data-testid="check-cols-missing">{(c.missing_required || []).join(", ") || "None"}</p></div>
    <div><span className="font-mono uppercase text-slate-500">Unknown columns (ignored)</span><p className="mt-1 text-amber-300" data-testid="check-cols-unknown">{(c.unknown || []).join(", ") || "None"}</p></div>
    <div><span className="font-mono uppercase text-slate-500">Optional not supplied</span><p className="mt-1 text-slate-400">{(c.missing_optional || []).join(", ") || "None"}</p></div>
  </div>
);

const Report = ({ r }) => {
  const pass = r.result === "PASS";
  return (
    <div className="space-y-4" data-testid="sap-check-report">
      <div className={`flex flex-wrap items-center gap-3 rounded-sm border px-5 py-4 ${pass ? "border-emerald-500/40 bg-emerald-500/10" : "border-rose-500/40 bg-rose-500/10"}`}>
        {pass ? <CheckCircle2 className="h-6 w-6 text-emerald-400" /> : <XCircle className="h-6 w-6 text-rose-400" />}
        <span className={`font-mono text-lg font-bold ${pass ? "text-emerald-300" : "text-rose-300"}`} data-testid="sap-check-result">{r.result}</span>
        <span className="text-sm text-slate-300" data-testid="sap-check-summary">{r.filename} · {r.rows} row(s) · {r.summary.orders} order(s): {r.summary.passed} pass, {r.summary.failed} fail{r.summary.duplicates ? `, ${r.summary.duplicates} already in Hub` : ""}</span>
        <Button variant="ghost" onClick={() => csv(r)} className={`ml-auto ${btn.ghost}`} data-testid="sap-check-download-btn"><Download className="mr-1.5 h-4 w-4" /> Download report</Button>
        {r.file_error && <p className="w-full text-sm text-rose-300" data-testid="sap-check-file-error">{r.file_error}</p>}
      </div>
      <Panel title="Columns"><Cols c={r.columns} /></Panel>
      {r.orders.length > 0 && (
        <Panel title="Orders">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-800"><tr><Th>SAP SO</Th><Th>Amazon PO</Th><Th>Lines</Th><Th>Ship-to</Th><Th>Site</Th><Th>Result</Th><Th>Errors / warnings</Th></tr></thead>
              <tbody className="divide-y divide-slate-800/70">
                {r.orders.map((o, i) => (
                  <tr key={i} data-testid={`sap-check-order-${i}`}>
                    <Td className="font-mono">{o.sales_order}</Td><Td className="font-mono">{o.po_number}</Td><Td>{o.lines}</Td><Td className="font-mono">{o.ship_to}</Td><Td className="font-mono">{o.site}</Td>
                    <Td className={`font-mono font-bold ${o.errors.length ? "text-rose-300" : "text-emerald-300"}`}>{o.errors.length ? "FAIL" : "PASS"}</Td>
                    <Td className="text-xs"><span className="text-rose-300">{o.errors.join(" · ")}</span>{o.errors.length > 0 && o.warnings.length > 0 && " · "}<span className="text-slate-400">{o.warnings.join(" · ")}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
};

export const SapChecker = () => {
  const [r, setR] = useState(null);
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">For the SAP team: upload a sample export to check it against the import layout and the Item Master. Nothing is created or stored.</p>
      <SapUpload onPreview={setR} path="/sap/check" testId="sap-check" hint="Drop a sample SAP export to check it — no orders will be created" />
      {r && <Report r={r} />}
    </div>
  );
};
